// Libs
import React, { useEffect, useState } from 'react';
import {
  useLazyQuery,
  NetworkStatus,
  useQuery,
  useApolloClient
} from '@apollo/client';
// import styled from 'styled-components';

// Components
import { BinderContent, Flexbox } from '@ibinder/common-frontend';
import SearchableDropDown from './SearchableDropDown';
import UploadFileButton from './UploadFileButton';

// Utils
import {
  GET_ALL_ORGS,
  GET_ALL_BINDERS,
  GET_FACILITY_STRUCTURES
} from '../../api/queries/queries_local';
import { getAppSetting } from '../../../Shared/ApplicationSettings';
import { binderTemplateDataSchema } from './FormValidation';
import {
  APIDOMAIN,
  apiEndpoint,
  MetadataElementIdConstants
} from '@ibinder/common-utils';

// Icons
import {
  check as checkIcon,
  close,
  exclamationCircle
} from '@ibinder/common-icons';

import {
  FormContainer,
  StyledButton,
  Icon,
  StyledDocumentActionsSnackbar,
  StyledToolButton
} from './form.styles';

import useImpersonateAdmin, {
  impersonateHeader,
  impersonateAdminnHeader
} from '../../utils/useImpersonateAdmin';

const CreateFacilityStructure = () => {
  const client = useApolloClient();

  const [appStatus, setAppStatus] = useState(null);
  const [responseMsg, setResponseMsg] = useState('');
  const [formCompleted, setFormCompleted] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [facilityId, setFacilityId] = useState(null);
  const [bindersData, setBindersData] = useState(null);
  const [selectedType, setSelectedType] = useState({
    organization: null,
    binders: null,
    facilityFile: null
  });
  const orgs = useQuery(GET_ALL_ORGS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const [useAdminToken] = useImpersonateAdmin(
    client,
    orgs.data?.get[selectedType.organization]?.id
  );

  const [getBinders, binders] = useLazyQuery(GET_ALL_BINDERS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (res) => setBindersData(res.get),
    ...impersonateHeader(useAdminToken)
  });

  const { data: facilityStructures } = useQuery(GET_FACILITY_STRUCTURES, {
    variables: {
      facilityId: facilityId
    },
    onCompleted: (res) => {
      if (res.get.length > 0) {
        setAppStatus('error');
        setResponseMsg('This binder already has a facility structure.');
      } else {
        setAppStatus(null);
        setResponseMsg('');
      }
    },
    skip: !facilityId
  });
  const facilityIdCheck =
    selectedType.binders !== null
      ? binders.data.get[selectedType.binders].metadata.filter(
          (i) =>
            i.metadataElementId === MetadataElementIdConstants.GLOBAL_FacilityId
        )
      : null;

  const getSelectedValues = () => {
    return {
      organizationId: orgs.data.get[selectedType.organization].id,
      facilityId,
      facilityFile: selectedType.facilityFile
    };
  };

  if (useAdminToken) console.log(useAdminToken);

  useEffect(() => {
    if (!useAdminToken) return;
    if (orgs.data && selectedType.organization !== null) {
      localStorage.setItem(
        'tenantId',
        orgs.data.get[selectedType.organization].id
      );
    }

    // Initial request to get organizations associated data
    // Refetch is run when reselection occurs
    if (!binders.data) {
      getBinders();
    } else {
      binders.refetch();
      setAppStatus(null);
      setResponseMsg('');
    }
  }, [useAdminToken]);

  useEffect(() => {
    // Reset other selections upon repicking an organization
    setFormCompleted(false);

    // Set selected organizations tenant id in localstorage so authLink can access it

    // Checks if selected binder has a facilityId
    if (selectedType.binders !== null) {
      if (facilityIdCheck.length > 0) {
        setFacilityId(facilityIdCheck[0].valueId);
        setAppStatus(null);
        setResponseMsg('');
      } else {
        setAppStatus('error');
        setResponseMsg('This binder does not have a Facility ID.');
      }
    }
  }, [selectedType.organization, selectedType.binders]);

  const UPLOAD_FILE = (adminData, file) => {
    // selectedType.facilityFile
    // This will upload the file after having read it
    const formDatas = new FormData();
    formDatas.append('file', file, file.name);
    const adminEnpdoint = apiEndpoint(getAppSetting('REACT_APP_API_URL'))(
      APIDOMAIN.admin
    );

    const URL = `${adminEnpdoint}/workflow/facilitystructure?organizationId=${adminData.organizationId}&facilityId=${adminData.facilityId}`;

    return fetch(URL, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'x-ibinder-tenantid': localStorage.getItem('tenantId'),
        'x-ibinder-organization-impersonate': useAdminToken
      },
      body: formDatas
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const adminData = getSelectedValues();
    console.log(adminData);
    setPostLoading(true);
    binderTemplateDataSchema
      .validate(adminData, { abortEarly: false })
      .then(() => {
        setResponseMsg('');

        UPLOAD_FILE(adminData, selectedType.facilityFile).then((res) => {
          if (res.status !== 200) {
            setResponseMsg(res.statusText);
            setAppStatus('error');
          }
          if (res.status === 200) {
            setSelectedType({
              organization: null,
              binders: null,
              facilityFile: null
            });
            setResponseMsg(res.statusText);
            setAppStatus('done');
          }
          setPostLoading(false);
        });
      });
  };

  return (
    <BinderContent loading={orgs.loading ? true : undefined}>
      <FormContainer onSubmit={handleSubmit} enctype="multipart/form-data">
        {appStatus && (
          <StyledDocumentActionsSnackbar
            state={appStatus === 'done' ? appStatus : 'error'}
            animationEnabled={true}
            action={
              <StyledToolButton
                type="button"
                onClick={() => setAppStatus(null)}
              >
                <span>{close}</span>
              </StyledToolButton>
            }
          >
            <Flexbox alignItems="center">
              <Icon>
                {appStatus === 'done' ? checkIcon : exclamationCircle}
              </Icon>
              <span>{responseMsg}</span>
            </Flexbox>
          </StyledDocumentActionsSnackbar>
        )}

        {orgs && (
          <SearchableDropDown
            {...{ selectedType, setSelectedType }}
            label="Organization"
            data={orgs.data && orgs.data.get}
            loading={orgs.networkStatus !== NetworkStatus.ready}
            type="organization"
          />
        )}
        {bindersData && selectedType.organization !== null && (
          <SearchableDropDown
            {...{ selectedType, setSelectedType }}
            label="Binders"
            data={bindersData}
            loading={binders.networkStatus !== NetworkStatus.ready}
            type="binders"
          />
        )}
        {selectedType.binders !== null && appStatus !== 'error' && (
          <div>
            <h2>Upload excel here</h2>
            <UploadFileButton {...{ setSelectedType, setFormCompleted }} />
          </div>
        )}

        <StyledButton
          data-test="submit-btn"
          btnType="regular"
          btnStyle="primary"
          label="Submit"
          type="submit"
          disabled={!formCompleted}
        />
        {postLoading && <div>Sending data...</div>}
      </FormContainer>
    </BinderContent>
  );
};

export default CreateFacilityStructure;
